import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Display timezone preference.
 *
 * The server stores and returns ALL timestamps as UTC (ISO with `Z`).
 * Timezone is purely a CLIENT-SIDE display concern: this store holds the
 * zone the user wants to read times in — which may be any IANA zone, not
 * necessarily their device's. `override = null` follows the device.
 *
 * Persisted per-device in localStorage. (If we later want it to follow the
 * user across devices, hydrate from /users/me preferences like
 * pdf/voice/a11y prefs — the server still only ever stores UTC.)
 */

/** Detected IANA zone of this device, e.g. "Africa/Cairo", "Asia/Dubai". */
export function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

interface TimezoneStore {
  /** User-chosen IANA zone, or null to follow the device. */
  override: string | null;
  setTimeZone: (tz: string | null) => void;
  resetToDevice: () => void;
}

export const useTimezoneStore = create<TimezoneStore>()(
  persist(
    (set) => ({
      override: null,
      setTimeZone: (tz) => set({ override: tz }),
      resetToDevice: () => set({ override: null }),
    }),
    { name: 'maie-display-tz' },
  ),
);

/** The effective IANA zone to display in: explicit override else device. */
export function useDisplayTimeZone(): string {
  const override = useTimezoneStore((s) => s.override);
  return override ?? deviceTimeZone();
}

/**
 * Non-hook accessor for util/service code (PDFs, exports, anything outside
 * a React render). Reads the same store so non-component formatting honors
 * the user's chosen zone too.
 */
export function getDisplayTimeZone(): string {
  return useTimezoneStore.getState().override ?? deviceTimeZone();
}
